import { useModal } from '@capturi/use-modal'
import { Box, Button, Flex, Heading, Text, VStack } from '@chakra-ui/react'
import { Trans } from '@lingui/macro'
import { FC } from 'react'
import { MdAdd } from 'react-icons/md'
import { CopilotConfig } from '../api/types'
import { QueueAndPromptModal } from './QueueAndPromptModal'
import { QueueAndPrompts } from './QueueAndPrompts'
import { duplicateQueues } from './duplicateQueues'

type LiveSummaryProps = {
  copilotConfig: CopilotConfig
  state: CopilotConfig
  setState: React.Dispatch<React.SetStateAction<CopilotConfig>>
}

export const LiveSummary: FC<LiveSummaryProps> = ({ state, setState }) => {
  const { queues, duplicates } = duplicateQueues(state.queuePrompts)

  const addQueuePromptPair = (newQueues: string[], newPrompt: string): void => {
    setState((prevState) => {
      return {
        ...prevState,
        queuePrompts: [
          ...prevState.queuePrompts,
          { queues: newQueues, prompt: newPrompt },
        ],
      }
    })
  }

  const handleDuplicateQueuePromptPair = (
    queues: string[],
    prompt: string,
  ): void => {
    setState((prevState) => {
      return {
        ...prevState,
        queuePrompts: [
          ...prevState.queuePrompts,
          { queues: queues, prompt: prompt },
        ],
      }
    })
  }

  const editQueuePromptPair = (
    newQueues: string[],
    newPrompt: string,
    index: number,
  ) => {
    setState((prevState) => {
      const updatedQueuePrompts = [...prevState.queuePrompts]
      updatedQueuePrompts[index] = { queues: newQueues, prompt: newPrompt }
      return {
        ...prevState,
        queuePrompts: updatedQueuePrompts,
      }
    })
  }
  const [openQueueAndPromptModal] = useModal(QueueAndPromptModal)

  const handleDeleteQueuePromptPair = (index: number) => {
    setState((prevState) => {
      return {
        ...prevState,
        queuePrompts: prevState.queuePrompts.toSpliced(index, 1),
      }
    })
  }

  return (
    <Box flex="1">
      <Box mb="4">
        <Flex justify="space-between">
          <Flex flexDirection="column" gap={2}>
            <Box>
              <Heading fontSize="3xl" fontStyle="italic">
                <Trans>Agent Co-pilot</Trans>
              </Heading>
            </Box>
            <Box>
              <Heading fontWeight="medium" fontSize="md">
                <Trans>Live summary</Trans>
              </Heading>
              <Text color="gray.600">
                <Trans>
                  Make a summary of the current conversation at any time to be
                  used to be saved to your conversation and used in your
                  external systems e.g. CRM system.
                </Trans>
              </Text>
            </Box>
            <Box>
              <Text color="gray.600" fontWeight="medium">
                <Trans>Live summary prompt per queues</Trans>
              </Text>
              <VStack gap={2}>
                {state.queuePrompts.map((obj, index) => {
                  return (
                    <QueueAndPrompts
                      key={index}
                      queues={obj.queues}
                      prompt={obj.prompt}
                      duplicatedQueues={duplicates}
                      handleDeleteQueuePromptPair={() =>
                        handleDeleteQueuePromptPair(index)
                      }
                      handleDuplicateQueuePromptPair={() =>
                        handleDuplicateQueuePromptPair(obj.queues, obj.prompt)
                      }
                      handleOpenModal={() =>
                        openQueueAndPromptModal({
                          queues: obj.queues,
                          prompt: obj.prompt,
                          existingQueues: queues,
                          duplicatedQueues: duplicates,
                          isNew: false,
                          handleAddOrEditQueuePromptPair: (
                            queues: string[],
                            prompt: string,
                          ) => editQueuePromptPair(queues, prompt, index),
                        })
                      }
                    />
                  )
                })}
              </VStack>
              <Button
                variant="ghost"
                color="primary.500"
                _hover={{ backgroundColor: 'transparent' }}
                _active={{ backgroundColor: 'transparent' }}
                leftIcon={<MdAdd />}
                onClick={() =>
                  openQueueAndPromptModal({
                    handleAddOrEditQueuePromptPair: (
                      queues: string[],
                      prompt: string,
                    ) => addQueuePromptPair(queues, prompt),
                    queues: [],
                    prompt: '',
                    isNew: true,
                    existingQueues: queues,
                    duplicatedQueues: duplicates,
                  })
                }
              >
                <Trans>Add queue/prompt pair</Trans>
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}
